<template>
  <div class="modal fade" id="modal_nfse" tabindex="-1" aria-hidden="true">
    <div class="modal-dialog modal-xl">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">{{ isEditing ? 'Editar NFSe' : 'Nova NFSe' }}</h5>
          <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Fechar"></button>
        </div>
        <form ref="nfseForm" @submit.prevent>
          <div class="modal-body">
            <!-- Dados da Emissão -->
            <div class="mb-5">
              <div class="d-flex justify-content-between align-items-center mb-3">
                <h6 class="fw-bold text-primary mb-0">Dados da Emissão</h6>
                <button type="button" class="btn btn-sm btn-light-primary"
                        @click="expandedSections.emissionDetails = !expandedSections.emissionDetails">
                  <i :class="expandedSections.emissionDetails ? 'bi bi-chevron-up' : 'bi bi-chevron-down'"></i>
                  <span class="ms-1">{{ expandedSections.emissionDetails ? 'Ocultar' : 'Mostrar' }} detalhes</span>
                </button>
              </div>

              <!-- Resumo da Emissão (sempre visível) -->
              <div class="p-3 bg-light rounded mb-3">
                <div class="d-flex flex-wrap gap-4">
                  <small class="text-muted">
                    <i class="bi bi-hash me-1"></i>
                    <strong>Série:</strong> {{ settings.nfse_rps_serie || '-' }}
                  </small>
                  <small class="text-muted">
                    <i class="bi bi-file-earmark-text me-1"></i>
                    <strong>Próximo RPS:</strong> {{ settings.nfse_rps_number || '-' }}
                  </small>
                  <small class="text-muted">
                    <i class="bi bi-calendar me-1"></i>
                    <strong>Competência:</strong> {{ formatDate(form.nfse_competence_date) }}
                  </small>
                  <small :class="settings.nfse_environment === '1' ? 'text-success' : 'text-warning'">
                    <i class="bi bi-cloud me-1"></i>
                    <strong>{{ settings.nfse_environment === '1' ? 'Produção' : 'Homologação' }}</strong>
                  </small>
                </div>
              </div>

              <!-- Campos detalhados (colapsável) -->
              <div class="collapse" :class="{ show: expandedSections.emissionDetails }" id="emissionDetailsCollapse">
                <div class="row">
                  <div class="col-md-3 mb-3">
                    <label class="form-label">Série do RPS</label>
                    <input type="text" class="form-control" v-model="settings.nfse_rps_serie" disabled>
                  </div>
                  <div class="col-md-3 mb-3">
                    <label class="form-label">Próximo Nº RPS</label>
                    <input type="text" class="form-control" v-model="settings.nfse_rps_number" disabled>
                  </div>
                  <div class="col-md-3 mb-3">
                    <label class="form-label required">Data de Competência</label>
                    <input type="date" class="form-control" v-model="form.nfse_competence_date" required>
                  </div>
                  <div class="col-md-3 mb-3">
                    <label class="form-label">Ambiente</label>
                    <input type="text" class="form-control" 
                           :value="settings.nfse_environment === '1' ? 'Produção' : 'Homologação'" disabled>
                  </div>
                </div>
              </div>
            </div>

            <div class="border-top mb-5"></div>

            <!-- Dados do Tomador -->
            <div class="mb-5">
              <div class="d-flex justify-content-between align-items-center mb-3">
                <h6 class="fw-bold text-primary mb-0">Dados do Tomador</h6>
                <button type="button" class="btn btn-sm btn-light-primary"
                        @click="expandedSections.takerDetails = !expandedSections.takerDetails">
                  <i :class="expandedSections.takerDetails ? 'bi bi-chevron-up' : 'bi bi-chevron-down'"></i>
                  <span class="ms-1">{{ expandedSections.takerDetails ? 'Ocultar' : 'Mostrar' }} detalhes</span>
                </button>
              </div>

              <!-- Busca de Cliente -->
              <div class="row">
                <div class="col-md-12 mb-3">
                  <label class="form-label">Buscar Cliente</label>
                  <div class="position-relative">
                    <input type="text" class="form-control" 
                           v-model="customerSearch" 
                           @input="searchCustomers"
                           @focus="showCustomerDropdown = true"
                           placeholder="Digite o nome, CPF/CNPJ ou e-mail do cliente...">
                    <div v-if="showCustomerDropdown && customers.length > 0" 
                         class="dropdown-menu show w-100" style="max-height: 250px; overflow-y: auto;">
                      <a class="dropdown-item" href="#" 
                         v-for="customer in customers" 
                         :key="customer.id"
                         @click.prevent="selectCustomer(customer)">
                        <div class="d-flex flex-column">
                          <span class="fw-bold">{{ customer.name }}</span>
                          <small class="text-muted">{{ formatDocument(customer.document) }} - {{ customer.email }}</small>
                        </div>
                      </a>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Resumo do Tomador (sempre visível) -->
              <div v-if="form.nfse_taker_name" class="mb-3 p-3 bg-light rounded">
                <div class="row">
                  <div class="col-md-6">
                    <small class="text-muted d-block">
                      <i class="bi bi-person me-1"></i>
                      <strong>{{ form.nfse_taker_name }}</strong>
                    </small>
                    <small class="text-muted d-block" v-if="form.nfse_taker_document">
                      <i class="bi bi-card-text me-1"></i>
                      {{ formatDocument(form.nfse_taker_document) }}
                    </small>
                  </div>
                  <div class="col-md-6">
                    <small class="text-muted d-block" v-if="form.nfse_taker_email">
                      <i class="bi bi-envelope me-1"></i>
                      {{ form.nfse_taker_email }}
                    </small>
                    <small class="text-muted d-block" v-if="form.nfse_taker_phone">
                      <i class="bi bi-telephone me-1"></i>
                      {{ form.nfse_taker_phone }}
                    </small>
                  </div>
                </div>
                <div class="row mt-2" v-if="form.nfse_taker_address">
                  <div class="col-md-12">
                    <small class="text-muted">
                      <i class="bi bi-geo-alt me-1"></i>
                      {{ form.nfse_taker_address }}<span v-if="form.nfse_taker_number">, {{ form.nfse_taker_number }}</span>
                      <span v-if="form.nfse_taker_neighborhood"> - {{ form.nfse_taker_neighborhood }}</span>
                      <span v-if="selectedCityName"> - {{ selectedCityName }}</span>
                      <span v-if="form.nfse_taker_zipcode"> - CEP: {{ form.nfse_taker_zipcode }}</span>
                    </small>
                  </div>
                </div>
              </div>

              <!-- Campos detalhados (colapsável) -->
              <div class="collapse" :class="{ show: expandedSections.takerDetails }" id="takerDetailsCollapse">
                <div class="row">
                  <div class="col-md-4 mb-3">
                    <label class="form-label required">CPF/CNPJ</label>
                    <input type="text" class="form-control" v-model="form.nfse_taker_document"
                           v-mask="['###.###.###-##', '##.###.###/####-##']" required>
                  </div>
                  <div class="col-md-8 mb-3">
                    <label class="form-label required">Nome/Razão Social</label>
                    <input type="text" class="form-control" v-model="form.nfse_taker_name" required>
                  </div>
                  <div class="col-md-6 mb-3">
                    <label class="form-label">E-mail</label>
                    <input type="email" class="form-control" v-model="form.nfse_taker_email">
                  </div>
                  <div class="col-md-6 mb-3">
                    <label class="form-label">Telefone</label>
                    <input type="text" class="form-control" v-model="form.nfse_taker_phone"
                           v-mask="['(##) ####-####', '(##) #####-####']">
                  </div>

                  <!-- Endereço -->
                  <div class="col-md-3 mb-3">
                    <label class="form-label">CEP</label>
                    <div class="input-group">
                      <input type="text" class="form-control" v-model="form.nfse_taker_zipcode"
                             v-mask="'#####-###'" @blur="searchCep">
                      <button type="button" class="btn btn-light-primary" @click="searchCep" 
                              :disabled="searchingCep" title="Buscar CEP">
                        <i class="bi" :class="searchingCep ? 'bi-hourglass-split' : 'bi-search'"></i>
                      </button>
                    </div>
                  </div>
                  <div class="col-md-7 mb-3">
                    <label class="form-label">Endereço</label>
                    <input type="text" class="form-control" v-model="form.nfse_taker_address">
                  </div>
                  <div class="col-md-2 mb-3">
                    <label class="form-label">Número</label>
                    <input type="text" class="form-control" v-model="form.nfse_taker_number">
                  </div>
                  <div class="col-md-4 mb-3">
                    <label class="form-label">Complemento</label>
                    <input type="text" class="form-control" v-model="form.nfse_taker_complement">
                  </div>
                  <div class="col-md-4 mb-3">
                    <label class="form-label">Bairro</label>
                    <input type="text" class="form-control" v-model="form.nfse_taker_neighborhood">
                  </div>
                  <div class="col-md-2 mb-3">
                    <label class="form-label">Estado</label>
                    <select class="form-select" v-model="form.id_taker_state" @change="onStateChange">
                      <option value="">UF</option>
                      <option v-for="state in states" :key="state.id_state" :value="state.id_state">
                        {{ state.state_initials }}
                      </option>
                    </select>
                  </div>
                  <div class="col-md-2 mb-3">
                    <label class="form-label">Cidade</label>
                    <select class="form-select" v-model="form.id_taker_city" :disabled="!form.id_taker_state">
                      <option value="">Selecione</option>
                      <option v-for="city in cities" :key="city.id_city" :value="city.id_city">
                        {{ city.city_name }}
                      </option>
                    </select>
                  </div>
                </div>
              </div>
            </div>

            <div class="border-top mb-5"></div>

            <!-- Dados do Serviço -->
            <div class="mb-5">
              <h6 class="fw-bold text-primary mb-3">Dados do Serviço</h6>
              <div class="row">
                <div class="col-md-4 mb-3">
                  <label class="form-label required">Valor do Serviço</label>
                  <div class="input-group">
                    <span class="input-group-text">R$</span>
                    <input type="number" step="0.01" class="form-control" 
                           v-model="form.nfse_service_value" required min="0.01">
                  </div>
                </div>
                <div class="col-md-4 mb-3">
                  <label class="form-label">Desconto Incondicionado</label>
                  <div class="input-group">
                    <span class="input-group-text">R$</span>
                    <input type="number" step="0.01" class="form-control" 
                           v-model="form.nfse_discount_unconditioned" min="0">
                  </div>
                </div>
                <div class="col-md-4 mb-3">
                  <label class="form-label">Alíquota ISS (%)</label>
                  <input type="number" step="0.01" class="form-control" 
                         v-model="form.nfse_iss_aliquot" min="0" max="100"
                         :placeholder="settings.nfse_iss_aliquot">
                </div>
                <div class="col-md-12 mb-3">
                  <label class="form-label">Descrição do Serviço</label>
                  <textarea class="form-control" v-model="form.nfse_service_description" rows="2"></textarea>
                </div>
              </div>
            </div>

            <div class="border-top mb-5"></div>

            <!-- Impostos e Retenções -->
            <div class="mb-5">
              <div class="d-flex justify-content-between align-items-center mb-3">
                <h6 class="fw-bold text-primary mb-0">Impostos e Retenções</h6>
                <button type="button" class="btn btn-sm btn-light-primary"
                        @click="expandedSections.taxDetails = !expandedSections.taxDetails">
                  <i :class="expandedSections.taxDetails ? 'bi bi-chevron-up' : 'bi bi-chevron-down'"></i>
                  <span class="ms-1">{{ expandedSections.taxDetails ? 'Ocultar' : 'Mostrar' }} detalhes</span>
                </button>
              </div>

              <!-- Resumo dos impostos -->
              <div class="p-3 bg-light rounded mb-3">
                <div class="d-flex flex-wrap gap-4">
                  <small class="text-muted">
                    <i class="bi bi-check-circle me-1" :class="form.nfse_iss_retained ? 'text-success' : ''"></i>
                    <strong>ISS Retido:</strong> {{ form.nfse_iss_retained ? 'Sim' : 'Não' }}
                  </small>
                  <small class="text-muted" v-if="form.nfse_pis_value > 0">
                    <strong>PIS:</strong> R$ {{ parseFloat(form.nfse_pis_value).toFixed(2) }}
                  </small>
                  <small class="text-muted" v-if="form.nfse_cofins_value > 0">
                    <strong>COFINS:</strong> R$ {{ parseFloat(form.nfse_cofins_value).toFixed(2) }}
                  </small>
                  <small class="text-muted" v-if="form.nfse_inss_value > 0">
                    <strong>INSS:</strong> R$ {{ parseFloat(form.nfse_inss_value).toFixed(2) }}
                  </small>
                  <small class="text-muted" v-if="form.nfse_ir_value > 0">
                    <strong>IRRF:</strong> R$ {{ parseFloat(form.nfse_ir_value).toFixed(2) }}
                  </small>
                  <small class="text-muted" v-if="form.nfse_csll_value > 0">
                    <strong>CSLL:</strong> R$ {{ parseFloat(form.nfse_csll_value).toFixed(2) }}
                  </small>
                  <small class="text-muted" v-if="!hasTaxValues">
                    <i class="bi bi-info-circle me-1"></i>
                    Nenhuma retenção informada
                  </small>
                </div>
              </div>

              <!-- Campos detalhados de impostos (colapsável) -->
              <div class="collapse" :class="{ show: expandedSections.taxDetails }" id="taxDetailsCollapse">
                <div class="row">
                  <div class="col-md-2 mb-3">
                    <div class="form-check mt-2">
                      <input class="form-check-input" type="checkbox" 
                             v-model="form.nfse_iss_retained" id="issRetained">
                      <label class="form-check-label" for="issRetained">ISS Retido</label>
                    </div>
                  </div>
                  <div class="col-md-2 mb-3">
                    <label class="form-label">PIS (R$)</label>
                    <input type="number" step="0.01" class="form-control" 
                           v-model="form.nfse_pis_value" min="0">
                  </div>
                  <div class="col-md-2 mb-3">
                    <label class="form-label">COFINS (R$)</label>
                    <input type="number" step="0.01" class="form-control" 
                           v-model="form.nfse_cofins_value" min="0">
                  </div>
                  <div class="col-md-2 mb-3">
                    <label class="form-label">INSS (R$)</label>
                    <input type="number" step="0.01" class="form-control" 
                           v-model="form.nfse_inss_value" min="0">
                  </div>
                  <div class="col-md-2 mb-3">
                    <label class="form-label">IRRF (R$)</label>
                    <input type="number" step="0.01" class="form-control" 
                           v-model="form.nfse_ir_value" min="0">
                  </div>
                  <div class="col-md-2 mb-3">
                    <label class="form-label">CSLL (R$)</label>
                    <input type="number" step="0.01" class="form-control" 
                           v-model="form.nfse_csll_value" min="0">
                  </div>
                </div>
              </div>
            </div>

            <!-- Info Adicional -->
            <div class="mb-3">
              <label class="form-label">Informações Complementares</label>
              <textarea class="form-control" v-model="form.nfse_additional_info" rows="2"></textarea>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-light" data-bs-dismiss="modal">Cancelar</button>
            <button type="button" class="btn btn-secondary" @click="saveDraft" :disabled="saving || emitting">
              <i class="bi bi-save me-1"></i>
              {{ saving ? 'Salvando...' : 'Salvar Rascunho' }}
            </button>
            <button type="button" class="btn btn-primary" @click="saveAndEmit" :disabled="saving || emitting">
              <i class="bi bi-send me-1"></i>
              {{ emitting ? 'Emitindo...' : 'Emitir NFSe' }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import { mask } from "vue-the-mask";
import http from "../../helpers/http";
import Alert from "../../helpers/alert";

export default {
  name: "ModalNfse",
  directives: { mask },
  emits: ['saved'],
  data() {
    return {
      modal: null,
      saving: false,
      emitting: false,
      isEditing: false,
      nfseId: null,
      form: this.getEmptyForm(),
      settings: {},
      customers: [],
      customerSearch: '',
      showCustomerDropdown: false,
      searchingCep: false,
      searchTimeout: null,
      states: [],
      cities: [],
      expandedSections: {
        emissionDetails: false,
        takerDetails: false,
        taxDetails: false,
      },
    };
  },
  computed: {
    place() {
      return this.$store.state.auth.place;
    },
    selectedCityName() {
      if (!this.form.id_taker_city) return '';
      const city = this.cities.find(c => c.id_city === this.form.id_taker_city);
      if (city) {
        const state = this.states.find(s => s.id_state === this.form.id_taker_state);
        return city.city_name + (state ? '/' + state.state_initials : '');
      }
      return '';
    },
    hasTaxValues() {
      return this.form.nfse_pis_value > 0 || 
             this.form.nfse_cofins_value > 0 || 
             this.form.nfse_inss_value > 0 || 
             this.form.nfse_ir_value > 0 || 
             this.form.nfse_csll_value > 0;
    },
  },
  mounted() {
    this.modal = new bootstrap.Modal(document.getElementById('modal_nfse'));
    document.addEventListener('click', this.handleClickOutside);
    this.loadStates();
  },
  beforeUnmount() {
    document.removeEventListener('click', this.handleClickOutside);
  },
  methods: {
    getEmptyForm() {
      return {
        nfse_competence_date: new Date().toISOString().split('T')[0],
        nfse_taker_document: '',
        nfse_taker_name: '',
        nfse_taker_email: '',
        nfse_taker_phone: '',
        nfse_taker_address: '',
        nfse_taker_number: '',
        nfse_taker_complement: '',
        nfse_taker_neighborhood: '',
        nfse_taker_zipcode: '',
        id_taker_state: '',
        id_taker_city: '',
        nfse_service_value: '',
        nfse_discount_unconditioned: 0,
        nfse_service_description: '',
        nfse_iss_aliquot: '',
        nfse_iss_retained: false,
        nfse_pis_value: 0,
        nfse_cofins_value: 0,
        nfse_inss_value: 0,
        nfse_ir_value: 0,
        nfse_csll_value: 0,
        nfse_additional_info: '',
      };
    },
    async open(nfse = null) {
      await this.loadSettings();
      
      // Resetar seções expandidas
      this.expandedSections = {
        emissionDetails: false,
        takerDetails: false,
        taxDetails: false,
      };
      
      if (nfse) {
        this.isEditing = true;
        this.nfseId = nfse.id_nfse;
        
        let stateId = '';
        if (nfse.taker_city) {
          stateId = nfse.taker_city.id_state;
          if (stateId) {
            await this.loadCities(stateId);
          }
        }
        
        this.form = {
          nfse_competence_date: nfse.nfse_competence_date,
          nfse_taker_document: nfse.nfse_taker_document,
          nfse_taker_name: nfse.nfse_taker_name,
          nfse_taker_email: nfse.nfse_taker_email || '',
          nfse_taker_phone: nfse.nfse_taker_phone || '',
          nfse_taker_address: nfse.nfse_taker_address || '',
          nfse_taker_number: nfse.nfse_taker_number || '',
          nfse_taker_complement: nfse.nfse_taker_complement || '',
          nfse_taker_neighborhood: nfse.nfse_taker_neighborhood || '',
          nfse_taker_zipcode: nfse.nfse_taker_zipcode || '',
          id_taker_state: stateId || '',
          id_taker_city: nfse.id_taker_city || '',
          nfse_service_value: nfse.nfse_service_value,
          nfse_discount_unconditioned: nfse.nfse_discount_unconditioned || 0,
          nfse_service_description: nfse.nfse_service_description || '',
          nfse_iss_aliquot: nfse.nfse_iss_aliquot || '',
          nfse_iss_retained: nfse.nfse_iss_retained || false,
          nfse_pis_value: nfse.nfse_pis_value || 0,
          nfse_cofins_value: nfse.nfse_cofins_value || 0,
          nfse_inss_value: nfse.nfse_inss_value || 0,
          nfse_ir_value: nfse.nfse_ir_value || 0,
          nfse_csll_value: nfse.nfse_csll_value || 0,
          nfse_additional_info: nfse.nfse_additional_info || '',
        };
      } else {
        this.isEditing = false;
        this.nfseId = null;
        this.form = this.getEmptyForm();
        this.cities = [];
        
        if (this.settings) {
          this.form.nfse_iss_aliquot = this.settings.nfse_iss_aliquot || '';
          this.form.nfse_iss_retained = this.settings.nfse_iss_retained || false;
          this.form.nfse_service_description = this.settings.nfse_service_description || '';
          this.form.nfse_additional_info = this.settings.nfse_additional_info || '';
        }
      }
      
      this.customerSearch = '';
      this.customers = [];
      this.modal.show();
    },
    async loadSettings() {
      try {
        const response = await http.get(`/places/${this.place.id_place}/nfse/settings`);
        this.settings = response.data || {};
      } catch (e) {
        console.error('Erro ao carregar configurações:', e);
      }
    },
    async loadStates() {
      try {
        const response = await http.get('/states');
        this.states = response.data || [];
      } catch (e) {
        console.error('Erro ao carregar estados:', e);
      }
    },
    async loadCities(stateId) {
      if (!stateId) {
        this.cities = [];
        return;
      }
      
      try {
        const response = await http.get(`/states/${stateId}/cities`);
        this.cities = response.data || [];
      } catch (e) {
        console.error('Erro ao carregar cidades:', e);
        this.cities = [];
      }
    },
    onStateChange() {
      this.form.id_taker_city = '';
      this.loadCities(this.form.id_taker_state);
    },
    searchCustomers() {
      clearTimeout(this.searchTimeout);
      
      if (this.customerSearch.length < 2) {
        this.customers = [];
        return;
      }
      
      this.searchTimeout = setTimeout(async () => {
        try {
          const response = await http.get(
            `/places/${this.place.id_place}/finances/customers/search`,
            { params: { q: this.customerSearch } }
          );
          this.customers = response.data || [];
          this.showCustomerDropdown = true;
        } catch (e) {
          console.error('Erro ao buscar clientes:', e);
        }
      }, 300);
    },
    async selectCustomer(customer) {
      this.form.nfse_taker_document = customer.document || '';
      this.form.nfse_taker_name = customer.name || '';
      this.form.nfse_taker_email = customer.email || '';
      this.form.nfse_taker_phone = customer.phone || customer.cellphone || '';
      this.form.nfse_taker_address = customer.street || '';
      this.form.nfse_taker_number = customer.number || '';
      this.form.nfse_taker_complement = customer.complement || '';
      this.form.nfse_taker_neighborhood = customer.neighborhood || '';
      this.form.nfse_taker_zipcode = customer.zipcode || '';
      
      if (customer.state_id) {
        this.form.id_taker_state = customer.state_id;
        await this.loadCities(customer.state_id);
        this.form.id_taker_city = customer.city_id || '';
      }
      
      this.customerSearch = customer.name;
      this.showCustomerDropdown = false;
      this.customers = [];
    },
    handleClickOutside(e) {
      if (!e.target.closest('.position-relative')) {
        this.showCustomerDropdown = false;
      }
    },
    async searchCep() {
      const cep = (this.form.nfse_taker_zipcode || '').replace(/\D/g, '');
      
      if (cep.length !== 8) {
        return;
      }
      
      this.searchingCep = true;
      
      try {
        const response = await fetch(`https://viacep.com.br/ws/${cep}/json/`);
        const data = await response.json();
        
        if (data.erro) {
          Alert.toastError('CEP não encontrado.');
          return;
        }
        
        this.form.nfse_taker_address = data.logradouro || '';
        this.form.nfse_taker_neighborhood = data.bairro || '';
        this.form.nfse_taker_complement = data.complemento || '';
        
        const state = this.states.find(s => s.state_initials === data.uf);
        if (state) {
          this.form.id_taker_state = state.id_state;
          await this.loadCities(state.id_state);

          // Aguardar um pouco para garantir que as cidades foram carregadas
          await new Promise(resolve => setTimeout(resolve, 100));

          const city = this.cities.find(c => String(c.id_city) === String(data.ibge));
          if (city) {
            this.form.id_taker_city = city.id_city;
          }
        }
      } catch (e) {
        Alert.toastError('Erro ao buscar CEP.');
      } finally {
        this.searchingCep = false;
      }
    },
    getFormData() {
      const data = { ...this.form };
      data.nfse_taker_document = data.nfse_taker_document.replace(/\D/g, '');
      data.nfse_taker_phone = (data.nfse_taker_phone || '').replace(/\D/g, '');
      data.nfse_taker_zipcode = (data.nfse_taker_zipcode || '').replace(/\D/g, '');
      delete data.id_taker_state;
      return data;
    },
    validateForm() {
      const errors = [];
      
      if (!this.form.nfse_competence_date) {
        errors.push('Data de Competência é obrigatória');
      }
      if (!this.form.nfse_taker_document || this.form.nfse_taker_document.replace(/\D/g, '').length < 11) {
        errors.push('CPF/CNPJ do Tomador é obrigatório');
      }
      if (!this.form.nfse_taker_name || this.form.nfse_taker_name.trim().length < 2) {
        errors.push('Nome/Razão Social do Tomador é obrigatório');
      }
      if (!this.form.nfse_service_value || parseFloat(this.form.nfse_service_value) <= 0) {
        errors.push('Valor do Serviço é obrigatório');
      }
      
      if (errors.length > 0) {
        // Expandir seções com campos inválidos
        if (!this.form.nfse_competence_date) {
          this.expandedSections.emissionDetails = true;
        }
        if (!this.form.nfse_taker_document || !this.form.nfse_taker_name) {
          this.expandedSections.takerDetails = true;
        }
        
        Alert.toastError(errors.join('<br>'));
        return false;
      }
      
      return true;
    },
    async saveDraft() {
      if (!this.validateForm()) {
        return;
      }
      
      this.saving = true;
      try {
        const data = this.getFormData();

        if (this.isEditing) {
          await http.put(`/places/${this.place.id_place}/nfse/${this.nfseId}`, data);
          Alert.toastSuccess('NFSe atualizada com sucesso!');
        } else {
          await http.post(`/places/${this.place.id_place}/nfse`, data);
          Alert.toastSuccess('NFSe criada com sucesso!');
        }

        this.$emit('saved');
        this.modal.hide();
      } catch (e) {
        const errorMessage = e.response?.data?.message ||
                           e.response?.data?.error ||
                           (e.response?.data?.errors && Object.values(e.response.data.errors).flat().join(', ')) ||
                           'Erro ao salvar NFSe.';
        Alert.toastError(errorMessage);
      } finally {
        this.saving = false;
      }
    },
    async saveAndEmit() {
      if (!this.validateForm()) {
        return;
      }
      
      this.emitting = true;
      try {
        const data = this.getFormData();
        let nfseId = this.nfseId;

        if (this.isEditing) {
          await http.put(`/places/${this.place.id_place}/nfse/${this.nfseId}`, data);
        } else {
          const response = await http.post(`/places/${this.place.id_place}/nfse`, data);
          nfseId = response.data.id_nfse;
        }

        await http.post(`/places/${this.place.id_place}/nfse/${nfseId}/emit`);
        Alert.toastSuccess('NFSe emitida com sucesso!');

        this.$emit('saved');
        this.modal.hide();
      } catch (e) {
        const errorMessage = e.response?.data?.error ||
                           e.response?.data?.message ||
                           (e.response?.data?.errors && Object.values(e.response.data.errors).flat().join(', ')) ||
                           'Erro ao emitir NFSe.';
        Alert.toastError(errorMessage);
      } finally {
        this.emitting = false;
      }
    },
    formatDocument(doc) {
      if (!doc) return '';
      const cleaned = (doc + '').replace(/\D/g, '');
      if (cleaned.length === 11) {
        return cleaned.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
      } else if (cleaned.length === 14) {
        return cleaned.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
      }
      return doc;
    },
    formatDate(date) {
      if (!date) return '-';
      const [year, month, day] = date.split('-');
      return `${day}/${month}/${year}`;
    },
  },
};
</script>

<style scoped>
.dropdown-menu {
  position: absolute;
  top: 100%;
  left: 0;
  z-index: 1050;
}
</style>
